import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import type { ActivatedRouteSnapshot, Params, UrlTree } from '@angular/router';
import _isEqual from 'lodash.isequal';
import { Memoize } from '../decorators/memoize/memoize.decorator';
import { Page } from '../models/app-config.model';
import { type Link, LinkType } from '../models/link.model';
import { RegexPattern } from '../models/regex-pattern.model';
import { WINDOW } from '../shared/tokens/window.token';
import { AppConfigStateService } from './app-config-state.service';
import { NavigationContext } from './navigation.context.service';
import { RoutesUtilityService } from './routes-utility.service';

@Injectable({ providedIn: 'root' })
export class NavigationUtilityService {
  static convertParamsToUpperCase(params: Params): Params {
    const queryParams: Params = {};

    Object.entries(params).forEach(([key, value]) => {
      queryParams[key.toUpperCase()] = value;
    });

    return queryParams;
  }

  private readonly window = inject(WINDOW);
  private readonly appConfigStateService = inject(AppConfigStateService);
  private readonly navigationContext = inject(NavigationContext);
  private readonly document = inject<Document>(DOCUMENT);

  isHomePage(url: string): boolean {
    const homepage = this.appConfigStateService.getHomepage();
    return RoutesUtilityService.pageNameURLMatch(homepage, url);
  }

  openLinkInNewWindow(link: Link): void {
    if (link.href) {
      if (link.type !== LinkType.EXTERNAL) {
        this.window.open(link.href);
      } else if (link.windowHeight && link.windowWidth) {
        const windowSize = `width=${link.windowWidth},height=${link.windowHeight}`;
        this.window.open(link.href, 'mywin', windowSize);
      } else {
        this.window.open(link.href, 'mywin');
      }
    }
  }

  getPage(url: string): Page | null {
    return this.appConfigStateService.getPage(RoutesUtilityService.sanitizeUrl(url));
  }

  getCurrentPage(): Page | null {
    return this.getPage(this.navigationContext.getUrl());
  }

  isValidUrl(url: string): boolean {
    return RegexPattern.URL.test(url.trim());
  }

  getOrigin(): string {
    return this.document.location.origin;
  }

  // don't memoize this for the time being. ActivatedRouteSnapshot has a circular reference
  // in some cases which fails the JSON.stringify
  areRoutesEqual(urlTree: UrlTree, route: ActivatedRouteSnapshot): boolean {
    if (urlTree && route) {
      return (
        this.normalizePath(RoutesUtilityService.sanitizeUrl(urlTree.toString())) === this.normalizePath(this.getPath(route)) &&
        _isEqual(
          NavigationUtilityService.convertParamsToUpperCase(urlTree.queryParams),
          NavigationUtilityService.convertParamsToUpperCase(route.queryParams)
        )
      );
    }
    return false;
  }

  getPath(route: ActivatedRouteSnapshot): string {
    return route?.url?.map(urlSegment => urlSegment.path).join('/') ?? '';
  }

  @Memoize()
  normalizePath(path: string | null = ''): string {
    return path ? path.replace(/^\/|\/$/g, '') : '';
  }
}
